import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  ModeType,
  TopLevelModes,
  getChildSubmodes,
  getModeByName,
} from "@/lib/modes";
import { Select, SelectContent, SelectItem } from "@/components/ui/select";

import { Button } from "@/components/ui/button";
import { SelectTrigger } from "@radix-ui/react-select";
import { useState, useEffect } from "react";

interface ModeCarrouselProps {
  mode: ModeType;
  setMode: (mode: ModeType) => void;
}

const slideItems = Object.values(TopLevelModes).filter(
  (m) => m !== TopLevelModes.Translate,
);

export function CarouselModeComponent({ mode, setMode }: ModeCarrouselProps) {
  const [api, setApi] = useState<CarouselApi>();

  const activeIndex = Math.max(
    0,
    slideItems.findIndex((m) => m === mode.topLevelMode),
  );

  // Scroll to the active mode when it changes after mount (e.g. browser back/forward)
  useEffect(() => {
    if (!api) return;
    api.scrollTo(activeIndex);
  }, [api, activeIndex]);

  const handleButtonClick = (parentMode: TopLevelModes) => {
    // If clicked on a button mode that doesn't have child submodes then set parent as mode
    if (getChildSubmodes(parentMode).length == 1) {
      const newMode = getModeByName(parentMode);
      if (!newMode) return;
      setMode(newMode);
    }
  };

  const handleSubmodeSelect = (value: string) => {
    const newMode = getModeByName(value);
    if (!newMode) return;
    setMode(newMode);
  };

  return (
    <div
      className="border border-border rounded-sm p-2"
      data-tour="mode-carousel"
    >
      <Carousel
        setApi={setApi}
        opts={{
          align: "center",
          loop: true,
          startIndex: activeIndex,
          slidesToScroll: 1,
          duration: 15,
          containScroll: "trimSnaps",
          dragFree: false,
        }}
        className="flex flex-row justify-between items-center"
      >
        <CarouselPrevious className="flex-shrink-0" />
        <CarouselContent className="grow min-w-0">
          {Object.values(TopLevelModes).map(
            (buttonMode) =>
              buttonMode !== TopLevelModes.Translate && (
                <CarouselItem
                  key={buttonMode}
                  // className=""
                  // className="sm:w-sm md:w-md lg:w-lg xl:w-xl 2xl:w-2xl"
                  // className="basis-2/7 pl-4" // Adjust button width relative to carousel
                  className="w-36 lg:w-48" // Adjust button width
                >
                  {getChildSubmodes(buttonMode).length == 1 ? (
                    // For modes without child submodes
                    <Button
                      variant={
                        buttonMode == mode.topLevelMode
                          ? "gradient"
                          : "secondary"
                      }
                      id={buttonMode}
                      className="w-full h-10 md:h-12"
                      aria-label={buttonMode}
                      onClick={() => handleButtonClick(buttonMode)}
                    >
                      {buttonMode}
                    </Button>
                  ) : (
                    // For modes with child submodes
                    <Select
                      value={buttonMode}
                      onValueChange={(value) => handleSubmodeSelect(value)}
                    >
                      <SelectTrigger className="w-full" asChild>
                        <Button
                          variant={
                            buttonMode == mode.topLevelMode
                              ? "gradient"
                              : "secondary"
                          }
                          id={buttonMode}
                          className="w-full h-10 md:h-12"
                          aria-label={buttonMode}
                        >
                          <span className="flex-col ml-2">
                            {buttonMode}
                            <br />
                            {mode.topLevelMode === buttonMode &&
                              mode.modeStr.split(".").length > 1 && (
                                <div>{mode.modeStr.split(".")[1]}</div>
                              )}
                          </span>
                        </Button>
                      </SelectTrigger>
                      <SelectContent
                        id="source-language-select"
                        position="popper"
                      >
                        {getChildSubmodes(buttonMode).map((submode) => (
                          <SelectItem
                            key={submode.modeStr}
                            value={submode.modeStr}
                          >
                            {submode.subMode}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </CarouselItem>
              ),
          )}
        </CarouselContent>

        <CarouselNext className="flex-shrink-0" />
      </Carousel>
    </div>
  );
}
